// Step 2 of My style: what the pasted emails cover and what Scout still asks for, with a box
// under each ask so the strategist answers it right there (P4-08). The server's thresholds
// decide `ready`; this only lays them out.

import { useState } from 'react';
import type { StyleAsk, StyleCoverage } from '../../api/types';
import {
  askWantsAnswer, askWantsEmail, hasAdditions, hintForAsk, sortedAsks, STYLE_LENGTHS, STYLE_SITUATIONS,
} from '../../lib/styleIntake';
import type { InlineAdditions, StyleAnswerKey } from '../../lib/styleIntake';

// The box for one more email that answers no particular ask.
const EXTRA_EMAIL = 'extra';

function askId(ask: StyleAsk): string {
  return `${ask.kind}:${ask.key}`;
}

function AnswerBox({ askKey, value, onChange }: { askKey: StyleAnswerKey; value: string; onChange: (value: string) => void }) {
  if (askKey === 'length') {
    return (
      <select value={value} onChange={(event) => onChange(event.target.value)} aria-label="Length">
        <option value="">Choose…</option>
        {STYLE_LENGTHS.map((length) => <option key={length.key} value={length.key}>{length.label}</option>)}
      </select>
    );
  }
  return <input value={value} onChange={(event) => onChange(event.target.value)} placeholder="Your answer" />;
}

export function StyleCoveragePanel({
  coverage,
  busyStep,
  hasProfile,
  onAdd,
  onBuild,
}: {
  coverage: StyleCoverage;
  busyStep: string | null;
  // Once a profile exists this panel is for adding more later, so building is not the page's main action.
  hasProfile: boolean;
  onAdd: (additions: InlineAdditions) => Promise<boolean>;
  onBuild: (allowGaps: boolean) => void;
}) {
  // Each ask's box, keyed by the ask, so a re-check that drops an answered ask drops its box too.
  const [emailText, setEmailText] = useState<Record<string, string>>({});
  const [answerText, setAnswerText] = useState<Partial<Record<StyleAnswerKey, string>>>({});
  const personaCount = Object.values(coverage.personas).filter((count) => count > 0).length;
  const asks = sortedAsks(coverage.asks);

  // What was typed under the asks, as one addition to the intake.
  const additions: InlineAdditions = { emails: [], answers: answerText };
  for (const ask of asks) {
    const text = emailText[askId(ask)];
    if (askWantsEmail(ask) && text?.trim()) additions.emails.push({ text, situationHint: hintForAsk(ask) });
  }
  const extraText = emailText[EXTRA_EMAIL] ?? '';
  if (extraText.trim()) additions.emails.push({ text: extraText, situationHint: '' });
  const typed = hasAdditions(additions);
  const checking = busyStep === 'check';
  const building = busyStep === 'compile';

  async function addAndCheck() {
    const saved = await onAdd(additions);
    if (saved) {
      setEmailText({});
      setAnswerText({});
    }
  }

  // One main action: add what was typed, else build. The other is a quiet link.
  const addIsPrimary = typed || !coverage.ready;
  const buildLabel = hasProfile ? 'Rebuild my style' : 'Build my style';

  return (
    <section className="stylestep">
      <h3>{coverage.ready ? 'Scout has what it needs' : 'What Scout still needs'}</h3>
      <p className="stylehint">
        {coverage.counted_email_count} emails counted · {Math.min(personaCount, 3)} of 3 kinds of buyer
      </p>
      <ul className="stylechips">
        {STYLE_SITUATIONS.map((situation) => {
          const count = coverage.situations[situation.key] ?? 0;
          const missing = coverage.missing_situations.includes(situation.key);
          return (
            <li key={situation.key} className={missing ? 'stylechip missing' : 'stylechip'}>
              {situation.label}: {count}
            </li>
          );
        })}
      </ul>

      {asks.length ? (
        <ul className="styleasks">
          {asks.map((ask) => (
            <li key={askId(ask)} className={ask.blocking ? 'blocking' : ''}>
              {ask.text}
              {askWantsEmail(ask) ? (
                <textarea
                  rows={4}
                  value={emailText[askId(ask)] ?? ''}
                  onChange={(event) => setEmailText({ ...emailText, [askId(ask)]: event.target.value })}
                  placeholder="Paste the email here, greeting to sign-off."
                  aria-label={ask.text}
                />
              ) : null}
              {askWantsAnswer(ask) ? (
                <AnswerBox
                  askKey={ask.key}
                  value={answerText[ask.key] ?? ''}
                  onChange={(value) => setAnswerText({ ...answerText, [ask.key]: value })}
                />
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      <label className="stylefield">
        {asks.some(askWantsEmail) ? 'Any other email (optional)' : 'Another email you liked (optional)'}
        <textarea
          rows={3}
          value={extraText}
          onChange={(event) => setEmailText({ ...emailText, [EXTRA_EMAIL]: event.target.value })}
          placeholder="Paste the email here, greeting to sign-off."
        />
      </label>

      <div className="styleactions">
        {addIsPrimary ? (
          <button type="button" className="btn primary" disabled={checking || !typed} onClick={addAndCheck}>
            {checking ? 'Scout is reading…' : 'Add and check again'}
          </button>
        ) : (
          <button type="button" className={hasProfile ? 'btn' : 'btn primary'} disabled={building} onClick={() => onBuild(false)}>
            {building ? 'Building your style…' : buildLabel}
          </button>
        )}
        {!coverage.ready ? (
          <button type="button" className="stylelink" disabled={building} onClick={() => onBuild(true)}>
            {building ? 'Building your style…' : hasProfile ? 'Rebuild with what I have' : 'Skip this and build with what I have'}
          </button>
        ) : null}
      </div>
    </section>
  );
}
